import React, { useRef, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronLeft, IconShare } from '@tabler/icons-react';
import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';
import { PDP_MOBILE_MODAL_BREAKPOINT_TIERS } from 'components/marketplace/helpers/presizedPhotos';
import { Map } from 'components/marketplace/property';
import { GalleryListingPhoto } from 'components/marketplace/shared/GalleryListingPhoto';
import { DynamicComponentWithCondition } from 'components/shared/dynamic';
import { useInViewAfterLoad } from 'components/shared/hooks/useInView';
import { formatCurrency } from 'helpers/formatters';

import type { InteractiveGoogleStreetViewProps } from '../../../properties/components/InteractiveGoogleStreetView';
import Modal from './Modal';
import { ModalContent } from './PhotoCarousel';

interface MobileModalContentProps {
  address?: string | null;
  closeModal: () => void;
  contentToShow: ModalContent;
  isModalOpen: boolean;
  latitude?: number | null;
  longitude?: number | null;
  openLightbox: (idx: number) => void;
  photos: GalleryPhoto[] | null | undefined;
  price?: number | null;
  openShareModal: () => void;
}

enum MapContent {
  MAP = 'Map',
  STREETVIEW = 'Street view',
}

export const MobileModalContent: React.FC<MobileModalContentProps> = React.memo(
  ({
    address,
    closeModal,
    contentToShow,
    isModalOpen,
    latitude,
    longitude,
    openLightbox,
    photos,
    price,
    openShareModal,
  }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [content, setContent] = useState<MapContent>(MapContent.MAP);
    const { inView: inViewMapPage } = useInViewAfterLoad({
      triggerOnce: true,
      initialInView: true,
      ref,
    });

    const getPhotoAlt = (photo: GalleryPhoto, idx: number) =>
      photo.isFloorPlan
        ? photo.label?.trim() || 'Floor plan'
        : `Property photo ${idx + 1}${address ? ` of ${address}` : ''}`;

    const switchToMap = () => setContent(MapContent.MAP);
    const switchToStreetView = () => setContent(MapContent.STREETVIEW);

    return (
      <Modal
        overflow={contentToShow === ModalContent.MAP ? 'hidden' : 'auto'}
        isOpen={isModalOpen}
        analyticsName="marketplace-mobile-photo-gallery-modal"
        onRequestClose={closeModal}
        headerProps={{
          rightComponent: (
            <Button
              variant="secondary"
              aria-label="share"
              analyticsName="marketplace-mobile-photo-gallery-share-button"
              onClick={openShareModal}
              className="size-7 rounded-full border-od-border-tertiary p-0"
            >
              <IconShare size={20} />
            </Button>
          ),
          title: price ? formatCurrency(price, { inDollars: true }) : 'Price not available',
          titleClassName: 'self-center pt-3 text-label-md',
          subtitleClassName: 'self-center text-body-sm text-od-content-secondary',
          subtitle: address || 'Address not available',
          leftComponent: (
            <Button
              variant="secondary"
              aria-label="back"
              analyticsName="marketplace-mobile-photo-gallery-back-button"
              onClick={closeModal}
              className="size-7 rounded-full border-od-border-tertiary p-0"
            >
              <IconChevronLeft size={20} />
            </Button>
          ),
        }}
      >
        <Col className="flex-1">
          {contentToShow === ModalContent.PHOTOS &&
            photos?.map((photo, idx) => (
              <Box
                key={`${idx}-photo`}
                onClick={() => openLightbox(idx)}
                analyticsName={
                  photo.isFloorPlan
                    ? 'marketplace-mobile-photo-gallery-floor-plan-clicked'
                    : 'marketplace-mobile-photo-gallery-photo-clicked'
                }
                aria-label={getPhotoAlt(photo, idx)}
                className="w-full flex-col pb-1.5"
              >
                <Box className={cn('aspect-[3/2] w-full overflow-hidden bg-od-bg-secondary')}>
                  <GalleryListingPhoto
                    photo={photo}
                    sizes={PDP_MOBILE_MODAL_BREAKPOINT_TIERS}
                    lazy={idx !== 0}
                    alt={getPhotoAlt(photo, idx)}
                    objectFit={photo.isFloorPlan ? 'contain' : 'cover'}
                    className="h-full w-full"
                    fit={photo.isFloorPlan ? 'contain' : 'cover'}
                  />
                </Box>
              </Box>
            ))}
          {contentToShow === ModalContent.MAP && latitude && longitude && (
            <Col className="flex-1">
              <Row className="h-13 w-full shrink-0 items-center justify-center bg-od-bg-primary">
                <Row
                  analyticsName="marketplace-mobile-modal-map-button"
                  aria-label="Map"
                  onClick={switchToMap}
                  className="h-full w-1/2 justify-center px-5"
                >
                  <Text
                    as="span"
                    className={cn(
                      'text-label-md',
                      content === MapContent.MAP
                        ? 'font-medium text-od-content-primary'
                        : 'font-normal text-od-content-tertiary',
                    )}
                  >
                    Map
                  </Text>
                </Row>
                <Row
                  analyticsName="marketplace-mobile-modal-street-view-button"
                  aria-label="Street view"
                  onClick={switchToStreetView}
                  className="h-full w-1/2 justify-center px-5"
                >
                  <Text
                    as="span"
                    className={cn(
                      'text-label-md',
                      content === MapContent.STREETVIEW
                        ? 'font-medium text-od-content-primary'
                        : 'font-normal text-od-content-tertiary',
                    )}
                  >
                    Street view
                  </Text>
                </Row>
              </Row>
              <Col className="flex-1">
                {content === MapContent.MAP && (
                  <Map key="property-location" latitude={latitude} longitude={longitude} />
                )}
                {content === MapContent.STREETVIEW && (
                  <Col className="h-full">
                    <DynamicComponentWithCondition<InteractiveGoogleStreetViewProps>
                      dynamicLoaderFunction={() =>
                        import('../../../properties/components/InteractiveGoogleStreetView').then(
                          (mod) => mod.InteractiveGoogleStreetView,
                        )
                      }
                      loadingCondition={inViewMapPage}
                      latitude={latitude}
                      longitude={longitude}
                      addressControl
                      linksControl
                      zoomControl
                    />
                  </Col>
                )}
              </Col>
            </Col>
          )}
        </Col>
      </Modal>
    );
  },
);

MobileModalContent.displayName = 'MobileModalContent';
